import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {AddressCodec, AddressEntity} from "../address";
import {RrcDistrictCode, toRrcDistrictCodeCode} from "./rrc-district-code";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {RrcAutoSignStrategy, toRrcAutoSignStrategy} from "./rrc-auto-sign-strategy";

export const RrcLocationProfileCodec = t.intersection([
  t.type({
    id: t.string,
    profileKey: t.string,
    effectiveStartDttm: t.number,
    version: t.number,
    status: t.string,
    locationId: t.string,
    locationType: t.string,
    rrcDistrict: t.string,
    snapshotJson: t.string,
    snapshotHash: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    supersedesId: t.string,
    supersededById: t.string,
    effectiveEndDttm: t.number,
    facilityName: t.string,
    facilityPermitNumber: t.string,
    leaseName: t.string,
    leaseNumber: t.string,
    gasPlantNumber: t.string,
    contactName: t.string,
    contactPhone: t.string,
    contactEmail: t.string,
    latCoord: t.number,
    longCoord: t.number,
    address: AddressCodec,
    commercial: t.boolean,
    autoEp9SignerId: t.string,
    autoEp9SignerName: t.string,
    autoEp9SignerStrategy: t.string,
    autoEp10SignerId: t.string,
    autoEp10SignerName: t.string,
    autoEp10SignerStrategy: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createdByName: t.string,
    lastModifiedByName: t.string,
    regionIds: t.array(t.string),
    locationName: t.string,
  }),
]);

export type RrcLocationProfile = t.TypeOf<typeof RrcLocationProfileCodec>;

@Exclude()
export class RrcLocationProfileEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  profileKey!: string;

  @Expose()
  effectiveStartDttm!: number;

  @Expose()
  effectiveEndDttm?: number;

  @Expose()
  version!: number;

  @Expose()
  status!: string;

  @Expose()
  supersedesId?: string;

  @Expose()
  supersededById?: string;

  @Expose()
  locationId!: string;

  @Expose()
  locationType!: string;

  @Expose()
  locationName?: string;

  @Transform(getDatabaseCodeTransformer(toRrcDistrictCodeCode))
  @Expose()
  rrcDistrict!: RrcDistrictCode;

  @Expose()
  facilityName?: string;

  @Expose()
  facilityPermitNumber?: string;

  @Expose()
  leaseName?: string;

  @Expose()
  leaseNumber?: string;

  @Expose()
  gasPlantNumber?: string;

  @Expose()
  contactName?: string;

  @Expose()
  contactPhone?: string;

  @Expose()
  contactEmail?: string;

  @Expose()
  latCoord?: number;

  @Expose()
  longCoord?: number;

  @Expose()
  @Type(() => AddressEntity)
  address?: AddressEntity

  @Expose()
  commercial?: boolean

  @Expose()
  autoEp9SignerId?: string;

  @Expose()
  autoEp9SignerName?: string;

  @Transform(getDatabaseCodeTransformer(toRrcAutoSignStrategy))
  @Expose()
  autoEp9SignerStrategy?: RrcAutoSignStrategy

  @Expose()
  autoEp10SignerId?: string;

  @Expose()
  autoEp10SignerName?: string;

  @Transform(getDatabaseCodeTransformer(toRrcAutoSignStrategy))
  @Expose()
  autoEp10SignerStrategy?: RrcAutoSignStrategy

  @Expose()
  snapshotJson!: string;

  @Expose()
  snapshotHash!: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  regionIds?: string[];

  @Expose()
  createdByName?: string;

  @Expose()
  lastModifiedByName?: string;

  readonly entityType = 'RRCLOCPROFILE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for RrcLocationProfileEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: RrcLocationProfile): RrcLocationProfileEntity {
    return plainToInstance(RrcLocationProfileEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<RrcLocationProfile> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RrcLocationProfileCodec, object);
  }

  asTypeValue(): RrcLocationProfile {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(RrcLocationProfileCodec, object);
  }
}

export interface RrcLocationProfileCreateDTO {
  profileKey?: string;
  effectiveStartDttm: number;
  effectiveEndDttm?: number
  locationId: string;
  locationType: string;
  rrcDistrict: string;
  facilityName?: string;
  facilityPermitNumber?: string;
  leaseName?: string;
  leaseNumber?: string;
  gasPlantNumber?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  latCoord?: number;
  longCoord?: number;
  address?: AddressEntity;
  commercial?: boolean;
  autoEp9SignerId?: string;
  autoEp9SignerStrategy?: string;
  autoEp10SignerId?: string;
  autoEp10SignerStrategy?: string;
}

export interface RrcLocationProfileUpdateDTO {
  id: string
  effectiveStartDttm: number;
  effectiveEndDttm?: number
  locationId: string;
  locationType: string;
  rrcDistrict: string;
  facilityName?: string;
  facilityPermitNumber?: string;
  leaseName?: string;
  leaseNumber?: string;
  gasPlantNumber?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  latCoord?: number;
  longCoord?: number;
  address?: AddressEntity;
  commercial?: boolean;
  autoEp9SignerId?: string;
  autoEp9SignerStrategy?: string;
  autoEp10SignerId?: string;
  autoEp10SignerStrategy?: string;
}
